/**
 * @file eventBus.js
 * @description Centralized event bus for module communication
 * @version 1.0.0
 * 
 * Usage:
 *   EventBus.emit('theme:changed', { theme: 'dark' });
 *   EventBus.on('theme:changed', (data) => console.log(data));
 */

class EventBus {
    constructor() {
        this.events = {};
        this.debugMode = false;
    }

    /**
     * Subscribe to an event
     * @param {string} eventName - Event to listen for
     * @param {Function} callback - Handler function
     * @returns {Function} Unsubscribe function
     */
    on(eventName, callback) {
        if (!this.events[eventName]) {
            this.events[eventName] = [];
        }

        this.events[eventName].push(callback);

        if (this.debugMode) {
            console.log(`[EventBus] Subscribed to: ${eventName}`);
        }

        // Return unsubscribe function
        return () => this.off(eventName, callback);
    }

    /**
     * Subscribe to an event (fires once, then unsubscribes)
     * @param {string} eventName - Event to listen for
     * @param {Function} callback - Handler function
     */
    once(eventName, callback) {
        const wrapper = (data) => {
            callback(data);
            this.off(eventName, wrapper);
        };
        this.on(eventName, wrapper);
    }

    /**
     * Unsubscribe from an event
     * @param {string} eventName - Event name
     * @param {Function} callback - Handler to remove
     */
    off(eventName, callback) {
        if (!this.events[eventName]) return;

        this.events[eventName] = this.events[eventName].filter(
            cb => cb !== callback
        );

        if (this.debugMode) {
            console.log(`[EventBus] Unsubscribed from: ${eventName}`);
        }
    }

    /**
     * Emit an event to all subscribers
     * @param {string} eventName - Event to emit
     * @param {*} data - Data to pass to handlers
     */
    emit(eventName, data) {
        if (!this.events[eventName]) {
            if (this.debugMode) {
                console.warn(`[EventBus] No listeners for: ${eventName}`);
            }
            return;
        }

        if (this.debugMode) {
            console.log(`[EventBus] Emitting: ${eventName}`, data);
        }

        this.events[eventName].forEach(callback => {
            try {
                callback(data);
            } catch (error) {
                console.error(`[EventBus] Error in ${eventName} handler:`, error);
            }
        });
    }

    /**
     * Remove all subscribers for an event
     * @param {string} eventName - Event name
     */
    clear(eventName) {
        if (eventName) {
            delete this.events[eventName];
        } else {
            this.events = {};
        }
    }

    /**
     * Get list of all events with subscriber counts
     * @returns {Object} Event names with subscriber counts
     */
    getEvents() {
        const eventInfo = {};
        for (const [name, callbacks] of Object.entries(this.events)) {
            eventInfo[name] = callbacks.length;
        }
        return eventInfo;
    }

    /**
     * Enable/disable debug logging
     * @param {boolean} enabled - Debug mode state
     */
    setDebug(enabled) {
        this.debugMode = enabled;
        console.log(`[EventBus] Debug mode: ${enabled ? 'ON' : 'OFF'}`);
    }
}

// Create singleton instance
const EventBusInstance = new EventBus();

// Export singleton
export default EventBusInstance;

// Export class for testing
export { EventBus };
